import { useEffect, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    Checkbox,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SyncIcon from '@mui/icons-material/Sync';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LinkIcon from '@mui/icons-material/Link';
import {
    ListBase,
    ListToolbar,
    Pagination,
    Title,
    useCanAccess,
    useGetList,
    useListContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';

import {
    assignTransactionUser,
    bulkAssignTransactions,
    classifyTransaction,
    ignoreTransaction,
    downloadChargesCsv,
    fetchEmployeeScoreboard,
    fetchUnassignedByCard,
    importAmexStatement,
    importChaseStatement,
    setCardAccountOwner,
    retryReceiptQboPush,
    returnTransaction,
    unlockReceipt,
    createQuickBooksProjectClass,
    type EmployeeScore,
    type UnassignedCard,
    type StatementImport,
    type ExpenseTransaction,
    type Receipt,
    type ReceiptAllocationChanges,
    type ReceiptCategory,
    type ReconciliationSummary,
    type ResponsibleUser,
} from './receiptApi';
import {
    AllocationFields,
    allocationChanges,
    allocationFrom,
    allocationReady,
    type Allocation,
} from './AllocationFields';
import { ReceiptDeleteDialog } from './ReceiptDeleteDialog';
import { ReceiptMatchDialog } from './ReceiptMatchDialog';
import { QUICK_RANGES } from './ReceiptFilterParts';
import {
    SUMMARY_LIST_FILTER,
    TRACKER_FILTERS,
    employeeGroups,
    quickDateRange,
    type EmployeeGroup,
    type QuickRange,
    type SimpleRecord,
    allocationColor,
    statusColor,
    type SummaryFilter,
} from './receiptHelpers';

type Props = {
    summary: ReconciliationSummary;
    users: ResponsibleUser[];
    categories: ReceiptCategory[];
    busy: boolean;
    canAdmin: boolean;
    matchExpenseId?: number | null;
    onImport: () => Promise<void>;
    onSyncQuickBooks: () => Promise<void>;
    onReconcile: () => Promise<void>;
    onRequest: (id: number) => Promise<void>;
    onAssign: (transactionId: number, userId: number | null) => Promise<boolean>;
    onMatch: (transactionId: number, receiptId: number) => Promise<boolean>;
    onViewReceipt: (receiptId: number) => Promise<void>;
    onReviewAllocation: (
        receiptId: number,
        decision: 'verified' | 'needs_correction',
        changes: ReceiptAllocationChanges
    ) => Promise<void>;
};

export const ReconciliationPanel = ({
    summary,
    users,
    categories,
    busy,
    canAdmin,
    matchExpenseId = null,
    onImport,
    onSyncQuickBooks,
    onReconcile,
    onRequest,
    onAssign,
    onMatch,
    onViewReceipt,
    onReviewAllocation,
}: Props) => {
    const refresh = useRefresh();
    const notify = useNotify();
    const [filter, setFilter] = useState<SummaryFilter | null>(null);
    const showingUnmatchedUploads = filter === 'unmatched_uploads';
    const [view, setView] = useState<'todo' | 'done' | 'ignored' | 'all'>('todo');
    const [employee, setEmployee] = useState<string>('all');
    const [range, setRange] = useState<QuickRange | 'all' | 'custom'>('all');
    const [customDates, setCustomDates] = useState({
        transaction_date_gte: '',
        transaction_date_lte: '',
    });
    const dates = useMemo(
        () =>
            range === 'all'
                ? {}
                : range === 'custom'
                  ? Object.fromEntries(
                        Object.entries(customDates).filter(([, value]) => value)
                    )
                  : quickDateRange(range),
        [range, customDates]
    );
    const groups = useMemo(() => employeeGroups(users), [users]);
    const [version, setVersion] = useState(0);
    const reloadKey = `${JSON.stringify(summary)}-${version}`;
    const reassigned = () => {
        setVersion(value => value + 1);
        refresh();
    };
    const showViewSelect = !showingUnmatchedUploads && filter !== 'approved';
    const listFilter = useMemo(
        () => ({
            ...(filter ? SUMMARY_LIST_FILTER[filter] : {}),
            ...(view === 'ignored'
                ? { ignored: true }
                : filter === 'approved' || view === 'all'
                  ? {}
                  : { done: view === 'done' }),
            ...(employee === 'all'
                ? {}
                : employee === 'unassigned'
                  ? { unassigned: true }
                  : { responsible_user_ids: employee }),
            ...dates,
        }),
        [filter, view, employee, dates]
    );
    const allCount =
        summary.matched + summary.discrepancies + summary.missing_receipts;
    const listTitle = showingUnmatchedUploads
        ? 'Unmatched uploads'
        : filter
          ? `AMEX and Visa transactions · ${filter.replaceAll('_', ' ')}`
          : 'AMEX and Visa transactions';

    const runAndRefresh = async (action: () => Promise<void>) => {
        await action();
        refresh();
    };

    return (
        <Stack spacing={3}>
            <Alert severity="info">
                Posted AMEX and Visa charges come from QuickBooks Online.
                For Review items must be Added in QuickBooks first
                (Uncategorized is enough). Local report import is only a
                fallback.
            </Alert>
            <Typography color="text.secondary">
                Saved in OneDrive: {summary.saved_receipts} receipts
                {summary.last_charge_date
                    ? ` · Newest synced charge date: ${summary.last_charge_date} (QuickBooks only sends purchases already Added in Banking)`
                    : ''}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                {(
                    [
                        ['all', allCount] as const,
                        ...TRACKER_FILTERS.map(
                            key => [key, summary[key]] as const
                        ),
                    ] as const
                ).map(([key, value]) => {
                    const selected =
                        key === 'all' ? filter === null : filter === key;
                    return (
                        <Card
                            key={key}
                            sx={{
                                flex: 1,
                                minWidth: 140,
                                outline: selected ? 2 : 0,
                                outlineColor: 'primary.main',
                            }}
                        >
                            <CardActionArea
                                onClick={() =>
                                    setFilter(key === 'all' ? null : key)
                                }
                            >
                                <CardContent>
                                    <Typography variant="h4">{value}</Typography>
                                    <Typography color="text.secondary">
                                        {key === 'posted'
                                            ? 'Posted by employee'
                                            : key === 'needs_account'
                                            ? 'Needs chart of account'
                                            : key === 'discrepancies'
                                              ? 'Matched with discrepancies'
                                              : key === 'approved'
                                                ? 'Approved (in QuickBooks)'
                                                : key.replaceAll('_', ' ')}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="h6">Employees</Typography>
                <Select
                    size="small"
                    value={range}
                    onChange={event => setRange(event.target.value as typeof range)}
                    inputProps={{ 'aria-label': 'Transaction dates' }}
                >
                    <MenuItem value="all">All dates</MenuItem>
                    {QUICK_RANGES.map(item => (
                        <MenuItem key={item.range} value={item.range}>
                            {item.label}
                        </MenuItem>
                    ))}
                    <MenuItem value="custom">Custom dates</MenuItem>
                </Select>
                {range === 'custom' && (
                    <>
                        <TextField
                            size="small"
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={customDates.transaction_date_gte}
                            onChange={event =>
                                setCustomDates({ ...customDates, transaction_date_gte: event.target.value })
                            }
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={customDates.transaction_date_lte}
                            onChange={event =>
                                setCustomDates({ ...customDates, transaction_date_lte: event.target.value })
                            }
                        />
                    </>
                )}
            </Stack>
            <EmployeeScoreboard
                groups={groups}
                dates={dates}
                reloadKey={reloadKey}
                selected={employee}
                onSelect={setEmployee}
            />
            {canAdmin && employee === 'unassigned' && (
                <UnassignedByCard groups={groups} reloadKey={reloadKey} onChanged={reassigned} />
            )}
            {canAdmin && (
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        disabled={busy}
                        onClick={() => void runAndRefresh(onSyncQuickBooks)}
                    >
                        Sync QuickBooks
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        disabled={busy}
                        onClick={() => void runAndRefresh(onReconcile)}
                    >
                        Reconcile
                    </Button>
                    <Button
                        variant="text"
                        startIcon={<UploadFileIcon />}
                        disabled={busy}
                        onClick={() => void runAndRefresh(onImport)}
                    >
                        Import local reports
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() =>
                            void downloadChargesCsv().catch(error =>
                                notify(
                                    error instanceof Error ? error.message : 'Could not download the charges.',
                                    { type: 'error' }
                                )
                            )
                        }
                    >
                        Download charges
                    </Button>
                </Stack>
            )}
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6">{listTitle}</Typography>
                {showViewSelect && (
                    <Select
                        size="small"
                        value={view}
                        onChange={event => setView(event.target.value as typeof view)}
                    >
                        <MenuItem value="todo">To do</MenuItem>
                        <MenuItem value="done">Done (in QuickBooks)</MenuItem>
                        <MenuItem value="ignored">Ignored (not expenses)</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                    </Select>
                )}
                {!showingUnmatchedUploads && (
                    <Select
                        size="small"
                        value={employee}
                        onChange={event => setEmployee(event.target.value)}
                        inputProps={{ 'aria-label': 'Employee' }}
                    >
                        <MenuItem value="all">All employees</MenuItem>
                        <MenuItem value="unassigned">Unassigned</MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.key} value={group.key}>
                                {group.label}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Stack>
            {showingUnmatchedUploads ? (
                <UnmatchedReceiptsList onViewReceipt={onViewReceipt} />
            ) : (
                <TransactionsList
                    key={`${filter || 'all'}-${view}-${employee}-${JSON.stringify(dates)}`}
                    listFilter={listFilter}
                    users={users}
                    categories={categories}
                    busy={busy}
                    canAdmin={canAdmin}
                    onRequest={id => void runAndRefresh(() => onRequest(id))}
                    onViewReceipt={onViewReceipt}
                    onReviewAllocation={(id, decision, changes) =>
                        void runAndRefresh(() =>
                            onReviewAllocation(id, decision, changes)
                        )
                    }
                    onAssigned={reassigned}
                />
            )}
            {matchExpenseId != null && (
                <MatchExpenseDialogHost
                    expenseId={matchExpenseId}
                    users={users}
                    busy={busy}
                    onAssign={async (transactionId, userId) => {
                        const result = await onAssign(transactionId, userId);
                        if (result) refresh();
                        return result;
                    }}
                    onMatch={async (transactionId, receiptId) => {
                        const result = await onMatch(transactionId, receiptId);
                        if (result) refresh();
                        return result;
                    }}
                />
            )}
        </Stack>
    );
};

const MatchExpenseDialogHost = ({
    expenseId,
    users,
    busy,
    onAssign,
    onMatch,
}: {
    expenseId: number;
    users: ResponsibleUser[];
    busy: boolean;
    onAssign: (transactionId: number, userId: number | null) => Promise<boolean>;
    onMatch: (transactionId: number, receiptId: number) => Promise<boolean>;
}) => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { data = [], isPending, error } = useGetList<ExpenseTransaction>(
        'expense_transactions',
        {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'id', order: 'DESC' },
            filter: { id: expenseId },
        }
    );
    const transaction = data[0];

    const close = () => navigate('/receipts/admin', { replace: true });

    useEffect(() => {
        if (isPending) return;
        if (transaction) return;
        notify(
            error instanceof Error ? error.message : 'Expense not found',
            { type: 'warning' }
        );
        navigate('/receipts/admin', { replace: true });
    }, [isPending, transaction, error, notify, navigate]);

    if (!transaction) return null;

    return (
        <ReceiptMatchDialog
            transaction={transaction}
            users={users}
            busy={busy}
            onClose={close}
            onAssign={onAssign}
            onMatch={onMatch}
        />
    );
};

const UnmatchedReceiptsList = ({
    onViewReceipt,
}: {
    onViewReceipt: (receiptId: number) => Promise<void>;
}) => (
    <ListBase
        resource="receipts"
        storeKey="receipts.reconciliation.unmatched"
        disableAuthentication
        filter={{ status: 'unmatched', deleted_at: null }}
        perPage={25}
        sort={{ field: 'id', order: 'DESC' }}
    >
        <UnmatchedReceiptsLayout onViewReceipt={onViewReceipt} />
    </ListBase>
);

const UnmatchedReceiptsLayout = ({
    onViewReceipt,
}: {
    onViewReceipt: (receiptId: number) => Promise<void>;
}) => {
    const { data = [], isPending, error, filterValues, setFilters } =
        useListContext<Receipt>();
    const { canAccess: canDelete } = useCanAccess({
        resource: 'receipt',
        action: 'delete',
    });
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const hasDateFilter = Boolean(
        filterValues?.transaction_date_gte || filterValues?.transaction_date_lte
    );

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Title title="Unmatched uploads" />
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
            >
                <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={filterValues?.transaction_date_gte || ''}
                    onChange={event => {
                        const next = { ...filterValues };
                        if (event.target.value) {
                            next.transaction_date_gte = event.target.value;
                        } else {
                            delete next.transaction_date_gte;
                        }
                        setFilters(next, undefined, false);
                    }}
                    sx={{ width: 160 }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={filterValues?.transaction_date_lte || ''}
                    onChange={event => {
                        const next = { ...filterValues };
                        if (event.target.value) {
                            next.transaction_date_lte = event.target.value;
                        } else {
                            delete next.transaction_date_lte;
                        }
                        setFilters(next, undefined, false);
                    }}
                    sx={{ width: 160 }}
                />
                {hasDateFilter && (
                    <Button
                        size="small"
                        onClick={() => {
                            const next = { ...filterValues };
                            delete next.transaction_date_gte;
                            delete next.transaction_date_lte;
                            setFilters(next, undefined, false);
                        }}
                    >
                        Clear dates
                    </Button>
                )}
            </Stack>
            {isPending ? null : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : !data.length ? (
                <Alert severity="info">
                    {hasDateFilter
                        ? 'No unmatched receipts in this date range.'
                        : 'No unmatched receipt uploads.'}
                </Alert>
            ) : (
                <>
                    <Card>
                        <Stack spacing={0}>
                            {data.map(receipt => (
                                <CardContent
                                    key={receipt.id}
                                    sx={{
                                        py: 1.5,
                                        '&:last-child': { pb: 1.5 },
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ sm: 'center' }}
                                        spacing={1}
                                    >
                                        <Box>
                                            <Typography fontWeight={600}>
                                                {receipt.merchant ||
                                                    receipt.original_filename}
                                                {receipt.amount
                                                    ? ` · $${receipt.amount}`
                                                    : ''}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {receipt.transaction_date ||
                                                    'No purchase date'}
                                                {' · '}
                                                {receipt.allocation_type ===
                                                'project_cost'
                                                    ? 'Project cost (COGS)'
                                                    : receipt.category}
                                                {receipt.project_no != null
                                                    ? ` · Project #${receipt.project_no}`
                                                    : ''}
                                            </Typography>
                                            {receipt.match_reason && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ mt: 0.5 }}
                                                    flexWrap="wrap"
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={receipt.match_reason.replaceAll(
                                                            '_',
                                                            ' '
                                                        )}
                                                        color={
                                                            [
                                                                'DUPLICATE_UPLOAD',
                                                                'CHARGE_ALREADY_MATCHED',
                                                                'NOT_A_RECEIPT',
                                                                'BILL_NOT_CARD',
                                                                'NOT_CARD_PAYMENT',
                                                            ].includes(
                                                                receipt.match_reason
                                                            )
                                                                ? 'warning'
                                                                : 'default'
                                                        }
                                                    />
                                                    {[
                                                        'DUPLICATE_UPLOAD',
                                                        'CHARGE_ALREADY_MATCHED',
                                                        'NOT_A_RECEIPT',
                                                        'BILL_NOT_CARD',
                                                        'NOT_CARD_PAYMENT',
                                                    ].includes(
                                                        receipt.match_reason
                                                    ) && (
                                                        <Typography
                                                            variant="caption"
                                                            color="warning.main"
                                                        >
                                                            Safe to delete
                                                            {receipt.duplicate_of_receipt_id
                                                                ? ` · copy of #${receipt.duplicate_of_receipt_id}`
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            )}
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    void onViewReceipt(
                                                        receipt.id
                                                    )
                                                }
                                            >
                                                View
                                            </Button>
                                            {canDelete && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        setDeletingId(
                                                            receipt.id
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            ))}
                        </Stack>
                    </Card>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
            <ReceiptDeleteDialog
                receiptId={deletingId}
                onClose={() => setDeletingId(null)}
            />
        </Stack>
    );
};

const TransactionsList = ({
    listFilter,
    users,
    categories,
    busy,
    canAdmin,
    onRequest,
    onViewReceipt,
    onReviewAllocation,
    onAssigned,
}: {
    listFilter?: Record<string, unknown>;
    users: ResponsibleUser[];
    categories: ReceiptCategory[];
    busy: boolean;
    canAdmin: boolean;
    onRequest: (id: number) => void;
    onViewReceipt: (receiptId: number) => Promise<void>;
    onReviewAllocation: (
        receiptId: number,
        decision: 'verified' | 'needs_correction',
        changes: ReceiptAllocationChanges
    ) => void;
    onAssigned: () => void;
}) => (
    <ListBase
        resource="expense_transactions"
        storeKey={
            listFilter
                ? `receipts.reconciliation.tx.${JSON.stringify(listFilter)}`
                : 'receipts.reconciliation.tx.all'
        }
        disableAuthentication
        filter={listFilter}
        perPage={25}
        sort={{ field: 'transaction_date', order: 'DESC' }}
    >
        <TransactionsLayout
            users={users}
            categories={categories}
            busy={busy}
            canAdmin={canAdmin}
            onRequest={onRequest}
            onViewReceipt={onViewReceipt}
            onReviewAllocation={onReviewAllocation}
            onAssigned={onAssigned}
        />
    </ListBase>
);

const TransactionsLayout = ({
    users,
    categories,
    busy,
    canAdmin,
    onRequest,
    onViewReceipt,
    onReviewAllocation,
    onAssigned,
}: {
    users: ResponsibleUser[];
    categories: ReceiptCategory[];
    busy: boolean;
    canAdmin: boolean;
    onRequest: (id: number) => void;
    onViewReceipt: (receiptId: number) => Promise<void>;
    onReviewAllocation: (
        receiptId: number,
        decision: 'verified' | 'needs_correction',
        changes: ReceiptAllocationChanges
    ) => void;
    onAssigned: () => void;
}) => {
    const { data = [], isPending, error, selectedIds, onToggleItem } =
        useListContext<ExpenseTransaction>();
    const navigate = useNavigate();
    const userName = useMemo(() => {
        const byId = new Map(
            users.map(user => [user.id, user.name || user.email] as const)
        );
        return (id: number | null) =>
            id == null ? 'Unassigned' : byId.get(id) || `User #${id}`;
    }, [users]);
    const groups = useMemo(() => employeeGroups(users), [users]);

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Title title="AMEX and Visa transactions" />
            <ListToolbar actions={false} />
            {canAdmin && !!data.length && (
                <BulkAssignBar users={users} onAssigned={onAssigned} />
            )}
            {isPending ? null : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : !data.length ? (
                <Alert severity="info">No transactions in this group.</Alert>
            ) : (
                <>
                    <Stack spacing={1}>
                        {data.map(transaction => (
                            <Card key={transaction.id} variant="outlined">
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {canAdmin && (
                                        <Checkbox
                                            checked={selectedIds.includes(transaction.id)}
                                            onChange={() => onToggleItem(transaction.id)}
                                            inputProps={{ 'aria-label': `Select ${transaction.description}` }}
                                        />
                                    )}
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ md: 'center' }}
                                        spacing={2}
                                        sx={{ flex: 1 }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <Box
                                                sx={{
                                                    minWidth: 74,
                                                    pr: 2,
                                                    borderRight: 1,
                                                    borderColor: 'divider',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {new Date(
                                                        `${transaction.transaction_date}T00:00:00`
                                                    )
                                                        .toLocaleDateString(
                                                            undefined,
                                                            { month: 'short' }
                                                        )
                                                        .toUpperCase()}
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    lineHeight={1}
                                                >
                                                    {Number(
                                                        transaction.transaction_date.slice(
                                                            -2
                                                        )
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {transaction.transaction_date.slice(
                                                        0,
                                                        4
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography fontWeight={600}>
                                                    {transaction.description} · $
                                                    {transaction.amount}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {transaction.source_network}
                                                    {transaction.card_last4
                                                        ? ` ••••${transaction.card_last4}`
                                                        : ''}
                                                    {transaction.card_account
                                                        ? ` (${transaction.card_account})`
                                                        : ''}
                                                    {' · '}
                                                    {transaction.transaction_date}
                                                    {' · '}
                                                    {userName(
                                                        transaction.responsible_user_id
                                                    ) ||
                                                        transaction.cardholder ||
                                                        'Unassigned'}
                                                    {transaction.expense_account
                                                        ? ` · ${transaction.expense_account}`
                                                        : ' · No chart of account'}
                                                </Typography>
                                                {transaction.discrepancy && (
                                                    <Typography
                                                        variant="body2"
                                                        color="warning.main"
                                                    >
                                                        {transaction.discrepancy}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            sx={{ flexWrap: 'wrap' }}
                                        >
                                            {canAdmin && (
                                                <PostedCharge
                                                    transaction={transaction}
                                                    categories={categories}
                                                    busy={busy}
                                                />
                                            )}
                                            {canAdmin &&
                                                transaction.receipt_id && (
                                                    <LinkedTransactionAllocation
                                                        receiptId={
                                                            transaction.receipt_id
                                                        }
                                                        categories={categories}
                                                        busy={busy}
                                                        onView={onViewReceipt}
                                                        onReview={
                                                            onReviewAllocation
                                                        }
                                                    />
                                                )}
                                            {canAdmin && (
                                                <TransactionRowActions
                                                    transaction={transaction}
                                                    groups={groups}
                                                    busy={busy}
                                                    onChanged={onAssigned}
                                                />
                                            )}
                                            <Chip
                                                size="small"
                                                label={
                                                    transaction.ignored_at
                                                        ? 'ignored'
                                                        : transaction.status.replaceAll('_', ' ')
                                                }
                                                color={
                                                    transaction.ignored_at
                                                        ? 'default'
                                                        : statusColor(transaction.status)
                                                }
                                            />
                                            {canAdmin &&
                                                !transaction.ignored_at &&
                                                transaction.status ===
                                                    'missing_receipt' && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<LinkIcon />}
                                                            disabled={busy}
                                                            onClick={() =>
                                                                navigate(
                                                                    `/receipts/admin/match/${transaction.id}`
                                                                )
                                                            }
                                                            sx={{ minHeight: '44px' }}
                                                        >
                                                            Match
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            startIcon={
                                                                <NotificationsActiveIcon />
                                                            }
                                                            disabled={
                                                                busy ||
                                                                !transaction.responsible_user_id
                                                            }
                                                            onClick={() =>
                                                                onRequest(
                                                                    transaction.id
                                                                )
                                                            }
                                                            sx={{ minHeight: '44px' }}
                                                        >
                                                            Request receipt
                                                        </Button>
                                                    </>
                                                )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
        </Stack>
    );
};

const LinkedTransactionAllocation = ({
    receiptId,
    categories,
    busy,
    onView,
    onReview,
}: {
    receiptId: number;
    categories: ReceiptCategory[];
    busy: boolean;
    onView: (receiptId: number) => Promise<void>;
    onReview: (
        receiptId: number,
        decision: 'verified' | 'needs_correction',
        changes: ReceiptAllocationChanges
    ) => void;
}) => {
    const { data: receipts = [] } = useGetList<Receipt>('receipts', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
        filter: { id: receiptId },
    });
    return (
        <TransactionAllocation
            receipt={receipts[0]}
            categories={categories}
            busy={busy}
            onView={onView}
            onReview={onReview}
        />
    );
};

const isDone = (receipt: Receipt) =>
    receipt.allocation_status === 'verified' &&
    !!receipt.qbo_pushed_at &&
    !receipt.qbo_push_error;

const QboPushStatus = ({ receipt }: { receipt: Receipt }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [sending, setSending] = useState(false);

    if (isDone(receipt)) {
        const unlock = async () => {
            if (!window.confirm('Unlock this receipt to make changes? Verify it again to re-send it to QuickBooks.')) return;
            setSending(true);
            try {
                await unlockReceipt(receipt.id);
                refresh();
            } catch (error) {
                notify(error instanceof Error ? error.message : 'Unlock failed', { type: 'error' });
            } finally {
                setSending(false);
            }
        };
        return (
            <>
                <Tooltip title={`Sent ${new Date(receipt.qbo_pushed_at!).toLocaleString()}`}>
                    <Chip size="small" color="success" variant="outlined" label="In QuickBooks" />
                </Tooltip>
                <Button size="small" disabled={sending} onClick={() => void unlock()}>
                    Unlock
                </Button>
            </>
        );
    }
    const send = async () => {
        setSending(true);
        try {
            const result = await retryReceiptQboPush(receipt.id);
            if (result.qbo_push_error) {
                notify(`QuickBooks push failed: ${result.qbo_push_error}`, { type: 'error' });
            } else {
                notify('Sent to QuickBooks', { type: 'success' });
            }
            refresh();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'QuickBooks push failed', {
                type: 'error',
            });
        } finally {
            setSending(false);
        }
    };
    return (
        <Tooltip title={receipt.qbo_push_error || 'Not sent to QuickBooks yet'}>
            <span>
                <Button
                    size="small"
                    color={receipt.qbo_push_error ? 'error' : 'primary'}
                    disabled={sending}
                    onClick={() => void send()}
                >
                    {receipt.qbo_push_error ? 'Retry QuickBooks' : 'Send to QuickBooks'}
                </Button>
            </span>
        </Tooltip>
    );
};

const TransactionAllocation = ({
    receipt,
    categories,
    busy,
    onView,
    onReview,
}: {
    receipt: Receipt | undefined;
    categories: ReceiptCategory[];
    busy: boolean;
    onView: (receiptId: number) => Promise<void>;
    onReview: (
        receiptId: number,
        decision: 'verified' | 'needs_correction',
        changes: ReceiptAllocationChanges
    ) => void;
}) => {
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [allocation, setAllocation] = useState<Allocation>(allocationFrom({}));
    const [notes, setNotes] = useState('');
    const [selectedProject, setSelectedProject] = useState<SimpleRecord | null>(null);
    const [creatingClass, setCreatingClass] = useState(false);
    const needsClass =
        allocation.allocationType === 'project_cost' &&
        !!allocation.projectId &&
        selectedProject != null &&
        !selectedProject.qbo_class_id;

    if (!receipt) return null;

    const openReview = () => {
        setAllocation(allocationFrom(receipt));
        setNotes(receipt.allocation_review_notes || '');
        setSelectedProject(null);
        setOpen(true);
    };
    const submit = async (decision: 'verified' | 'needs_correction') => {
        await onReview(
            receipt.id,
            decision,
            decision === 'verified'
                ? { ...allocationChanges(allocation), notes: notes.trim() || undefined }
                : { notes: notes.trim() || undefined }
        );
        setOpen(false);
    };
    const createClass = async () => {
        if (!selectedProject) return;
        setCreatingClass(true);
        try {
            const created = await createQuickBooksProjectClass(selectedProject.id);
            setSelectedProject({
                ...selectedProject,
                qbo_class_id: created.qbo_class_id,
                legacy_mrt_construction_qb: false,
            });
            notify(
                selectedProject.legacy_mrt_construction_qb
                    ? 'Class created in MRT Roofing QuickBooks (was Legacy MRT Construction QB)'
                    : 'Class created in MRT Roofing QuickBooks',
                { type: 'success' }
            );
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Could not create Class',
                { type: 'error' }
            );
        } finally {
            setCreatingClass(false);
        }
    };

    return (
        <>
            <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 180 }}>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || isDone(receipt)}
                    onClick={openReview}
                >
                    Project &amp; Expense
                </Button>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {receipt.allocation_type === 'project_cost'
                        ? `COGS · #${receipt.project_id || 'Project needed'}`
                        : `Expense · ${receipt.category}`}
                </Typography>
            </Stack>
            <Chip
                size="small"
                label={receipt.allocation_status.replaceAll('_', ' ')}
                color={allocationColor(receipt.allocation_status)}
            />
            {receipt.allocation_status === 'verified' && (
                <QboPushStatus receipt={receipt} />
            )}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Verify project and expense</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Review the allocation selected by the employee, edit it if
                        needed, then verify it to send it to QuickBooks.
                    </Typography>
                    <Stack spacing={2}>
                        <AllocationFields
                            value={allocation}
                            onChange={setAllocation}
                            categories={categories}
                            onProjectSelect={setSelectedProject}
                        />
                        {needsClass && (
                            <Alert severity="warning">
                                {selectedProject?.legacy_mrt_construction_qb
                                    ? 'Legacy MRT Construction QB — create a Class in MRT Roofing before verifying.'
                                    : 'This job has no QuickBooks Class yet. Create one in MRT Roofing before verifying.'}
                                <Box mt={1}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={busy || creatingClass}
                                        onClick={() => void createClass()}
                                    >
                                        Create Class in MRT Roofing
                                    </Button>
                                </Box>
                            </Alert>
                        )}
                        <TextField
                            multiline
                            minRows={2}
                            label="Bookkeeper note"
                            value={notes}
                            onChange={event => setNotes(event.target.value)}
                            helperText="Required when returning the allocation to the employee."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => void onView(receipt.id)}>
                        View receipt
                    </Button>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        color="warning"
                        disabled={busy || !notes.trim()}
                        onClick={() => void submit('needs_correction')}
                    >
                        Return to employee
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={
                            busy ||
                            needsClass ||
                            !allocationReady(allocation, categories)
                        }
                        onClick={() => void submit('verified')}
                    >
                        Save &amp; verify
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const PostedCharge = ({
    transaction,
    categories,
    busy,
}: {
    transaction: ExpenseTransaction;
    categories: ReceiptCategory[];
    busy: boolean;
}) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState<'classify' | 'return' | null>(null);
    const [allocation, setAllocation] = useState<Allocation>(allocationFrom({}));
    const [note, setNote] = useState('');

    if (transaction.qbo_pushed_at) {
        return (
            <Tooltip title={`Sent ${new Date(transaction.qbo_pushed_at).toLocaleString()}`}>
                <Chip size="small" color="success" variant="outlined" label="In QuickBooks" />
            </Tooltip>
        );
    }
    const run = async (action: () => Promise<unknown>, message: string) => {
        setSaving(true);
        try {
            await action();
            notify(message, { type: 'success' });
            setMode(null);
            refresh();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not save', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };
    return (
        <>
            {transaction.posted_at && (
                <Tooltip title={transaction.post_note || 'Posted by the employee'}>
                    <Chip size="small" color="info" label="Posted by employee" />
                </Tooltip>
            )}
            {!transaction.receipt_id && (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || saving}
                    onClick={() => {
                        setAllocation(allocationFrom(transaction));
                        setMode('classify');
                    }}
                >
                    Classify
                </Button>
            )}
            {transaction.posted_at && !transaction.receipt_id && (
                <Button
                    size="small"
                    color="warning"
                    disabled={busy || saving}
                    onClick={() => {
                        setNote('');
                        setMode('return');
                    }}
                >
                    Return to employee
                </Button>
            )}
            <Dialog open={mode !== null} onClose={() => setMode(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {mode === 'return' ? 'Return to employee' : 'Verify charge without receipt'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {transaction.description} · ${transaction.amount}
                        {transaction.post_note ? ` · Employee note: ${transaction.post_note}` : ''}
                    </Typography>
                    <Stack spacing={2}>
                        {mode === 'classify' ? (
                            <AllocationFields value={allocation} onChange={setAllocation} categories={categories} />
                        ) : (
                            <TextField
                                multiline
                                minRows={2}
                                required
                                label="What should the employee fix?"
                                value={note}
                                onChange={event => setNote(event.target.value)}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMode(null)}>Cancel</Button>
                    {mode === 'classify' ? (
                        <Button
                            variant="contained"
                            color="success"
                            disabled={saving || !allocationReady(allocation, categories)}
                            onClick={() =>
                                void run(
                                    () => classifyTransaction(transaction.id, allocationChanges(allocation)),
                                    'Sent to QuickBooks'
                                )
                            }
                        >
                            Save &amp; send to QuickBooks
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="warning"
                            disabled={saving || !note.trim()}
                            onClick={() =>
                                void run(() => returnTransaction(transaction.id, note.trim()), 'Returned to employee')
                            }
                        >
                            Return
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

const money = (value: number) =>
    value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const BUCKETS = [
    ['missing', 'Missing receipts'],
    ['pending', 'Pending admin approval'],
    ['approved', 'Approved (in QuickBooks)'],
] as const;

const EmployeeScoreboard = ({
    groups,
    dates,
    reloadKey,
    selected,
    onSelect,
}: {
    groups: EmployeeGroup[];
    dates: Record<string, string>;
    reloadKey: unknown;
    selected: string;
    onSelect: (key: string) => void;
}) => {
    const [scores, setScores] = useState<EmployeeScore[]>([]);
    useEffect(() => {
        let live = true;
        fetchEmployeeScoreboard(dates)
            .then(rows => live && setScores(rows))
            .catch(() => live && setScores([]));
        return () => {
            live = false;
        };
    }, [dates, reloadKey]);

    const rows = [
        ...groups,
        { key: 'unassigned', label: 'Unassigned', ids: [] as number[] },
    ].map(group => {
        const mine = scores.filter(score =>
            group.key === 'unassigned'
                ? score.responsible_user_id == null
                : group.ids.includes(score.responsible_user_id ?? -1)
        );
        const total = (field: keyof EmployeeScore) =>
            mine.reduce((sum, score) => sum + Number(score[field] || 0), 0);
        return {
            ...group,
            buckets: BUCKETS.map(([bucket]) => ({
                count: total(`${bucket}_count`),
                amount: total(`${bucket}_amount`),
            })),
        };
    });

    return (
        <Card variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Employee</TableCell>
                        {BUCKETS.map(([bucket, label]) => (
                            <TableCell key={bucket} align="right">
                                {label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow
                            key={row.key}
                            hover
                            selected={selected === row.key}
                            onClick={() => onSelect(selected === row.key ? 'all' : row.key)}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell>{row.label}</TableCell>
                            {row.buckets.map((cell, index) => (
                                <TableCell key={BUCKETS[index][0]} align="right">
                                    {cell.count} · {money(cell.amount)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
};

const TransactionRowActions = ({
    transaction,
    groups,
    busy,
    onChanged,
}: {
    transaction: ExpenseTransaction;
    groups: EmployeeGroup[];
    busy: boolean;
    onChanged: () => void;
}) => {
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    const ignored = !!transaction.ignored_at;
    const current = groups.find(
        group => transaction.responsible_user_id != null && group.ids.includes(transaction.responsible_user_id)
    );

    const save = async (action: () => Promise<unknown>, message: string) => {
        setSaving(true);
        try {
            await action();
            notify(message, { type: 'success' });
            onChanged();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not save', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const assign = (key: string) => {
        const group = groups.find(item => item.key === key);
        if (group) void save(() => assignTransactionUser(transaction.id, group.primaryId), `Assigned to ${group.label}`);
    };

    return (
        <>
            {!ignored && (
                <Select
                    size="small"
                    displayEmpty
                    value={current?.key ?? ''}
                    disabled={busy || saving}
                    onChange={event => assign(event.target.value)}
                    inputProps={{ 'aria-label': `Assign ${transaction.description} to` }}
                >
                    <MenuItem value="" disabled>
                        Assign to…
                    </MenuItem>
                    {groups.map(group => (
                        <MenuItem key={group.key} value={group.key}>
                            {group.label}
                        </MenuItem>
                    ))}
                </Select>
            )}
            <Tooltip title={ignored ? 'Back to the to-do list' : 'Not an expense (e.g. card payment); stays in QuickBooks'}>
                <span>
                    <Button
                        size="small"
                        color={ignored ? 'primary' : 'inherit'}
                        disabled={busy || saving || (!ignored && (!!transaction.receipt_id || !!transaction.qbo_pushed_at))}
                        onClick={() =>
                            void save(() => ignoreTransaction(transaction.id, !ignored), ignored ? 'Restored' : 'Ignored')
                        }
                        sx={{ minHeight: '44px' }}
                    >
                        {ignored ? 'Restore' : 'Ignore'}
                    </Button>
                </span>
            </Tooltip>
        </>
    );
};

const BulkAssignBar = ({
    users,
    onAssigned,
}: {
    users: ResponsibleUser[];
    onAssigned: () => void;
}) => {
    const { data = [], selectedIds, onSelect, onUnselectItems } =
        useListContext<ExpenseTransaction>();
    const notify = useNotify();
    const groups = useMemo(() => employeeGroups(users), [users]);
    const [target, setTarget] = useState('');
    const [saving, setSaving] = useState(false);
    const pageIds = data.map(transaction => transaction.id);
    const allOnPage = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

    const assign = async () => {
        const group = groups.find(item => item.key === target);
        if (!group) return;
        setSaving(true);
        try {
            const { assigned } = await bulkAssignTransactions(selectedIds as number[], group.primaryId);
            notify(`Assigned ${assigned} charges to ${group.label}`, { type: 'success' });
            onUnselectItems();
            onAssigned();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Assign failed', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Checkbox
                checked={allOnPage}
                indeterminate={!allOnPage && selectedIds.length > 0}
                onChange={() => (allOnPage ? onUnselectItems() : onSelect(pageIds))}
                inputProps={{ 'aria-label': 'Select all on this page' }}
            />
            <Typography variant="body2" color="text.secondary">
                {selectedIds.length ? `${selectedIds.length} selected` : 'Select all on this page'}
            </Typography>
            {!!selectedIds.length && (
                <>
                    <Select
                        size="small"
                        displayEmpty
                        value={target}
                        onChange={event => setTarget(event.target.value)}
                        inputProps={{ 'aria-label': 'Assign to' }}
                    >
                        <MenuItem value="" disabled>
                            Assign to…
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.key} value={group.key}>
                                {group.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button variant="contained" size="small" disabled={!target || saving} onClick={() => void assign()}>
                        Assign
                    </Button>
                </>
            )}
        </Stack>
    );
};

const StatementImportButton = ({
    label,
    upload,
    onChanged,
}: {
    label: string;
    upload: (file: File, apply: boolean) => Promise<StatementImport>;
    onChanged: () => void;
}) => {
    const notify = useNotify();
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<StatementImport | null>(null);
    const [busy, setBusy] = useState(false);

    const run = async (next: File, apply: boolean) => {
        setBusy(true);
        try {
            const result = await upload(next, apply);
            if (!apply) {
                setFile(next);
                setPreview(result);
                return;
            }
            const waiting = result.matched - result.assigned;
            notify(
                `Assigned ${result.assigned} charges. Linked ${result.receipts_linked} receipts.` +
                    (result.statement_only ? ` ${result.statement_only} are on the statement only.` : '') +
                    (waiting ? ` ${waiting} charges wait for their card in Users.` : ''),
                { type: 'success' }
            );
            setPreview(null);
            onChanged();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not read the statement', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button size="small" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                {label}
                <input
                    hidden
                    type="file"
                    accept="application/pdf"
                    onChange={event => {
                        const next = event.target.files?.[0];
                        event.target.value = '';
                        if (next) void run(next, false);
                    }}
                />
            </Button>
            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>{label} {preview?.period}</DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Card</TableCell>
                                <TableCell>On statement</TableCell>
                                <TableCell>MRT user</TableCell>
                                <TableCell align="right">Unassigned charges matched</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {preview?.cards.map(card => (
                                <TableRow key={card.card_last4}>
                                    <TableCell>••••{card.card_last4}</TableCell>
                                    <TableCell>{card.cardholder}</TableCell>
                                    <TableCell>
                                        {card.user_name ?? (
                                            <Typography variant="body2" color="warning.main">
                                                Add ••••{card.card_last4} in Users
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {card.matched} · {money(Number(card.matched_amount))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {preview?.receipts_linked} receipts match one charge within 14 days.{' '}
                        {preview?.statement_only} are on the statement and not in QuickBooks.{' '}
                        {preview?.disagreed} fit two different charges and stay unmatched.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={busy || !(preview?.matched || preview?.receipts_linked)}
                        onClick={() => file && void run(file, true)}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const UnassignedByCard = ({
    groups,
    reloadKey,
    onChanged,
}: {
    groups: EmployeeGroup[];
    reloadKey: string;
    onChanged: () => void;
}) => {
    const notify = useNotify();
    const [rows, setRows] = useState<UnassignedCard[]>([]);
    useEffect(() => {
        let live = true;
        fetchUnassignedByCard()
            .then(next => live && setRows(next))
            .catch(() => live && setRows([]));
        return () => {
            live = false;
        };
    }, [reloadKey]);

    const setOwner = async (account: string, key: string) => {
        const group = groups.find(item => item.key === key);
        if (!group) return;
        if (!window.confirm(`Assign all charges from "${account}" to ${group.label}, now and on every Sync?`)) return;
        try {
            const { assigned } = await setCardAccountOwner(account, group.primaryId);
            notify(`Assigned ${assigned} charges to ${group.label}`, { type: 'success' });
            onChanged();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not save', { type: 'error' });
        }
    };

    return (
        <Card variant="outlined" sx={{ overflowX: 'auto' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5 }}>
                <Typography variant="subtitle1">Why these charges are Unassigned</Typography>
                <Stack direction="row" spacing={1}>
                    <StatementImportButton label="Upload Chase statement" upload={importChaseStatement} onChanged={onChanged} />
                    <StatementImportButton label="Upload American Express statement" upload={importAmexStatement} onChanged={onChanged} />
                </Stack>
            </Stack>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>QuickBooks card account</TableCell>
                        <TableCell>Card</TableCell>
                        <TableCell align="right">Charges</TableCell>
                        <TableCell>Fix</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={`${row.card_account}-${row.card_last4}`}>
                            <TableCell>{row.card_account || 'Unknown (run Sync QuickBooks)'}</TableCell>
                            <TableCell>{row.card_last4 ? `••••${row.card_last4}` : 'No card number'}</TableCell>
                            <TableCell align="right">
                                {row.count} · {money(Number(row.amount))}
                            </TableCell>
                            <TableCell>
                                {row.card_last4 && !row.card_in_users ? (
                                    <Typography variant="body2" color="warning.main">
                                        Add card ••••{row.card_last4} to its owner in Users
                                    </Typography>
                                ) : row.card_account ? (
                                    <Select
                                        size="small"
                                        displayEmpty
                                        value=""
                                        onChange={event => void setOwner(row.card_account!, event.target.value)}
                                        inputProps={{ 'aria-label': 'Account owner' }}
                                    >
                                        <MenuItem value="" disabled>
                                            Whole account belongs to…
                                        </MenuItem>
                                        {groups.map(group => (
                                            <MenuItem key={group.key} value={group.key}>
                                                {group.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Select them in the list below and Assign
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
};
